import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import HeaderTitleContent from '../../../../common/components/atom/HeaderTitleContent/HeaderTitleContent'
import FilterV2 from '../../../../features/filter/components/FilterV2'
import { useDefaultFilter } from '../../../../common/hooks/useDefaultFilter'
import { useSingleMinigrid } from '../../../../features/minigrid/hook'
import momiAPI from '../../../../features/momi/service/momiAPI'
import { useQuery } from 'react-query'
import MomiTelemetryLineChartPresenter from '../../../../features/minigrid/components/LiveEnergyDashboard/MomiTelemetryLineChartPresenter'
import MomiTelemetryMultiLineChartPresenter from '../../../../features/minigrid/components/LiveEnergyDashboard/MomiTelemetryMultiLineChartPresenter'
import MomiLastValuesMetrics from '../../../../features/minigrid/components/LiveEnergyDashboard/MomiLastValuesMetrics'
import {
  chartsForSegment,
  isLiveEnergySegment,
  isMomiMultiLineSpec,
  LiveEnergySegment,
  MomiChartSpec,
  momiChartSpecKey,
  pageTitleForSegment,
} from '../../../../features/minigrid/liveEnergyDashboardConfig'
import { MomiTelemetryFilter } from '../../../../features/minigrid/service/momiTelemetryAPI'
import styles from './MinigridLiveEnergyDashboardPage.module.scss'

export default function MinigridLiveEnergyDashboardPage() {
  const { minigridId, energySegment } = useParams<{
    minigridId: string
    energySegment: string
  }>()
  const navigate = useNavigate()
  const { filter: globalFilter } = useDefaultFilter(7, '1h')
  const {
    singleMinigridQuery: { data: minigrid },
  } = useSingleMinigrid(minigridId)

  const segment: LiveEnergySegment | null = isLiveEnergySegment(energySegment)
    ? energySegment
    : null

  const momiId = minigrid?.minigrid_momi_id?.trim() || ''

  const momiQuery = useQuery({
    queryKey: ['momi-for-live', momiId],
    queryFn: () => momiAPI.getSingleMomi(momiId),
    enabled: Boolean(momiId),
  })

  const momiUuid = momiQuery.data?.momi_uuid?.trim() || ''

  const handleGoBack = () => {
    if (minigridId) {
      navigate(`/main/minigrid/${minigridId}`)
      return
    }
    navigate(-1)
  }

  const telemetryFilter: MomiTelemetryFilter = {
    startDate: globalFilter.startDate ?? '',
    stopDate: globalFilter.stopDate ?? '',
    aggregateWindow: globalFilter.aggregateWindow ?? '1h',
  }

  const [selectedChart, setSelectedChart] = useState<{
    component: React.ReactNode
    title: string
  } | null>(null)

  const closeModal = () => setSelectedChart(null)

  if (!segment) {
    return (
      <div className={styles.page}>
        <HeaderTitleContent
          withGoBackBtn
          goBackBtnLabel="Retour"
          onGoBack={handleGoBack}
        >
          Segment inconnu
        </HeaderTitleContent>
        <p className={styles.hint}>URL de segment invalide.</p>
      </div>
    )
  }

  const charts = chartsForSegment(segment)
  const pageLabel = pageTitleForSegment(segment)

  const chartForSpec = (spec: MomiChartSpec) =>
    isMomiMultiLineSpec(spec) ? (
      <MomiTelemetryMultiLineChartPresenter
        momiUuid={momiUuid}
        title={spec.title}
        unit={spec.unit}
        lines={spec.multiLine!}
        filter={telemetryFilter}
        scale={spec.scale}
      />
    ) : (
      <MomiTelemetryLineChartPresenter
        momiUuid={momiUuid}
        measurement={spec.measurement}
        title={spec.title}
        label={spec.label}
        unit={spec.unit}
        filter={telemetryFilter}
        scale={spec.scale}
      />
    )

  return (
    <div className={styles.page}>
      <HeaderTitleContent
        withGoBackBtn
        goBackBtnLabel="Retour au minigrid"
        onGoBack={handleGoBack}
      >
        {minigrid?.minigrid_name ?? 'Minigrid'} — {pageLabel}
      </HeaderTitleContent>

      {!momiId && (
        <div className={styles.banner}>
          Associez un MoMi EMS à ce minigrid. Les courbes Influx attendent le tag{' '}
          <code>uuid</code> égal à l’UUID gateway MoMi (line protocol).
        </div>
      )}

      {momiId && momiQuery.isError && (
        <div className={styles.banner}>
          Impossible de charger le MoMi. Vérifiez l’identifiant MoMi du minigrid.
        </div>
      )}

      {momiId && !momiUuid && momiQuery.isSuccess && (
        <div className={styles.banner}>
          Ce MoMi n’a pas d’UUID renseigné : impossible d’interroger Influx (tag{' '}
          <code>uuid</code>).
        </div>
      )}

      <div className={styles.mainContent}>
        <div className={styles.leftContent}>
          <div className={styles.filterSection}>
            <FilterV2 />
          </div>

          {momiUuid && (
            <>
            <MomiLastValuesMetrics momiUuid={momiUuid} charts={charts} />
            <div className={styles.chartsGrid}>
              {charts.map((spec) => (
                <div
                  key={momiChartSpecKey(spec)}
                  className={styles.chartWrapper}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedChart({
                        title: spec.title,
                        component: chartForSpec(spec),
                      })
                    }
                  }}
                  onClick={() =>
                    setSelectedChart({
                      title: spec.title,
                      component: chartForSpec(spec),
                    })
                  }
                >
                  {chartForSpec(spec)}
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </div>

      {selectedChart && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>{selectedChart.title}</span>
              <button type="button" className={styles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>{selectedChart.component}</div>
          </div>
        </div>
      )}
    </div>
  )
}
